import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import PriceLadderTable from "./PriceLadderTable";
import { InvoiceWasteSummary } from "../../../types/invoice-waste";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface PriceLadderButtonProps {
  summary: InvoiceWasteSummary | null;
  loading: boolean;
}

/**
 * The สรุปยอดหักตามราคา ladder, folded behind one icon next to the tab bar.
 *
 * It is a cross-check the branch runs against their own sheet, not part of either
 * report, so it sits beside รายงานการขาย / ใบจ่ายสินค้า and opens on demand instead of
 * pushing the tables down the page. The same ladder answers for whichever tab is open —
 * the caller owns the fetch, so switching tabs re-points it without this component
 * knowing which tab it is on.
 *
 * Nothing renders while the summary is in flight or absent: a report with no waste
 * checker has no ladder at all, and an icon that opens an empty modal is worse than no
 * icon.
 */
export default function PriceLadderButton({
  summary,
  loading,
}: PriceLadderButtonProps) {
  const [open, setOpen] = useState<boolean>(false);

  if (loading || !summary || summary.price_groups.length === 0) return null;

  return (
    <>
      <Tooltip title="สรุปยอดหักตามราคา">
        <IconButton
          size="small"
          color="primary"
          aria-label="สรุปยอดหักตามราคา"
          onClick={() => setOpen(true)}
          sx={{ mb: 1 }}
        >
          <SummarizeOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: { sx: { [compact]: { maxHeight: "94vh", m: 2 } } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>สรุปยอดหักตามราคา</DialogTitle>
        <DialogContent dividers>
          <DialogContentText variant="caption" sx={{ display: "block", mb: 1 }}>
            จำนวน = ขาด/เกินกี่ชิ้นเทียบกับที่ขายได้จริง (ติดลบ = ขาด) ·
            หักเฉพาะรายการที่ขาด ราคาที่เกินไม่คิดเงิน
          </DialogContentText>
          <PriceLadderTable summary={summary} loading={false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
